import {
  mdiArrowLeft,
  mdiCheckCircle,
  mdiClockOutline,
  mdiCloseCircle,
  mdiHelpCircleOutline,
  mdiHistory,
  mdiKeyboardReturn,
  mdiPencilOutline,
  mdiRefresh,
  mdiSubdirectoryArrowRight,
  mdiTextBoxSearchOutline
} from '@mdi/js'

import type { PostStationDomainKey } from '@/Network/Types/Responses/PostStationResponses'

import { PostStationStatus } from './types'
import type {
  ComplianceState,
  PostStationCompliance,
  PostStationRow,
  PostStationStatusColor,
  RegistrationStatusValue
} from './types'
import type { PostStationConsolidatedStatus } from '@/Network/Types/Responses/PostStationResponses'
import {
  EFFECTIVE_REGISTRATION_STATUS_LABELS,
  EffectiveRegistrationStatus,
  REGISTRATION_NOT_EVALUATED_LABEL
} from '@/constants/ds-attendance.constants'

export interface StatusConfig {
  label: string
  color: PostStationStatusColor
  icon: string
}

export const STATUS_CONFIG: Record<PostStationStatus, StatusConfig> = {
  [PostStationStatus.APPROVED]: { label: 'Aprovado', color: 'green', icon: mdiCheckCircle },
  [PostStationStatus.REVIEW]: { label: 'Revisar', color: 'orange', icon: mdiHistory },
  [PostStationStatus.IN_EVALUATION]: {
    label: 'Em validação',
    color: 'blue',
    icon: mdiClockOutline
  },
  [PostStationStatus.IN_ANALYSIS]: { label: 'Em análise', color: 'yellow', icon: mdiPencilOutline },
  [PostStationStatus.REVERTED]: { label: 'Revertido', color: 'gray', icon: mdiArrowLeft }
}

// Status novo do backend antes do FE acompanhar cai em badge neutro — nunca `undefined`.
export const UNKNOWN_STATUS_CONFIG: StatusConfig = {
  label: 'Desconhecido',
  color: 'gray',
  icon: mdiHelpCircleOutline
}

// Os valores do enum já são os do contrato da API, então o lookup por valor é
// seguro em runtime; o cast só faz a ponte de tipos (union de literais → enum).
export const statusConfigFor = (status: PostStationConsolidatedStatus): StatusConfig =>
  STATUS_CONFIG[status as PostStationStatus] ?? UNKNOWN_STATUS_CONFIG

export type PostStationRowActionKey =
  | 'details'
  | 'open-station'
  | 'history'
  | 'revert'
  | 'revalidate'

// Chaves do usePostStationPermissions que gateiam ações do menu ⋯.
export type PostStationActionPermissionKey = 'canRevert' | 'canRevalidate'

export interface RowActionConfig {
  key: PostStationRowActionKey
  label: string
  icon: string
  // Ausente = visível em qualquer status da linha.
  visibleForStatus?: PostStationStatus[]
  // Ausente = sem gate de permissão.
  permission?: PostStationActionPermissionKey
}

export const ROW_ACTIONS: RowActionConfig[] = [
  { key: 'details', label: 'Ver detalhes', icon: mdiTextBoxSearchOutline },
  { key: 'open-station', label: 'Abrir plantão', icon: mdiSubdirectoryArrowRight },
  { key: 'history', label: 'Histórico', icon: mdiHistory },
  {
    key: 'revert',
    label: 'Reverter avaliação',
    icon: mdiKeyboardReturn,
    // Só faz sentido desfazer o que já está aprovado (RN-06).
    visibleForStatus: [PostStationStatus.APPROVED],
    permission: 'canRevert'
  },
  {
    key: 'revalidate',
    label: 'Revalidar plantão',
    icon: mdiRefresh,
    // Revalidação manual só faz sentido depois de uma reversão (RF-15).
    visibleForStatus: [PostStationStatus.REVERTED],
    permission: 'canRevalidate'
  }
]

/**
 * Ações do menu ⋯ visíveis para uma linha: filtra `ROW_ACTIONS` por status da
 * linha e por permissão do usuário. Helper puro para o comportamento ficar
 * coberto por unit test, em vez de ficar inline no template.
 *
 * O cast do status é seguro em runtime porque os valores do enum são os do
 * contrato da API — um status novo do backend simplesmente não casa com nenhum
 * `visibleForStatus`, então a ação gateada por status fica escondida.
 */
export const visibleRowActions = (
  status: PostStationConsolidatedStatus,
  permissions: Record<PostStationActionPermissionKey, boolean>
): RowActionConfig[] =>
  ROW_ACTIONS.filter((action) => {
    const matchesStatus =
      !action.visibleForStatus || action.visibleForStatus.includes(status as PostStationStatus)
    const hasPermission = !action.permission || permissions[action.permission]
    return matchesStatus && hasPermission
  })

// Tamanho mínimo da justificativa de reversão (RN-06). Espelha o `@MinLength(20)`
// do backend: aqui é feedback contínuo (botão desabilitado), lá é a barreira real.
export const REVERT_JUSTIFICATION_MIN_LENGTH = 20

// Espelha o ColumnConfig do TableModular. Duplicado de propósito: importar o type
// nomeado de um .vue dentro de um .ts quebra o build (ts-loader só vê o default).
interface ColumnConfig {
  key: string
  label: string
  width?: string
  align?: 'left' | 'center'
}

// Cada key tem um slot `column-{key}` correspondente no PostStationTable.
// As 5 colunas de domínio são `align: 'center'` para o cabeçalho ficar no mesmo
// eixo da bolinha de conformidade (que já é centralizada na célula).
export const COLUMNS: ColumnConfig[] = [
  // Largura fixa (e não `minmax(…, Nfr)`): um id não ganha nada crescendo, e uma
  // fração roubaria espaço de "Médico"/"Unidade" nas telas largas. 90px comportam
  // o cabeçalho e o valor com folga — o grid já beira o limite de 1366px.
  { key: 'plantao', label: 'Plantão', width: '90px', align: 'left' },
  { key: 'medico', label: 'Médico', width: 'minmax(240px, 1.4fr)' },
  { key: 'unidade', label: 'Unidade / Escala', width: 'minmax(160px, 1fr)' },
  { key: 'inicio', label: 'Início', width: 'minmax(120px, 0.8fr)' },
  { key: 'regPlantao', label: 'Reg. Plantão', width: 'minmax(110px, 0.7fr)', align: 'center' },
  { key: 'docQual', label: 'Doc. Qual.', width: 'minmax(100px, 0.7fr)', align: 'center' },
  { key: 'repasse', label: 'Repasse', width: 'minmax(90px, 0.6fr)', align: 'center' },
  { key: 'excep', label: 'Excep.', width: 'minmax(90px, 0.6fr)', align: 'center' },
  // Mesma largura de "Doc. Qual.": os dois rótulos têm 10 caracteres, e os
  // 80px anteriores (a coluna mais estreita do grid) truncariam este.
  { key: 'pgto', label: 'Pol. Pgto.', width: 'minmax(100px, 0.7fr)', align: 'center' },
  { key: 'status', label: 'Status', width: 'minmax(140px, 0.9fr)' }
]

// Nome da unidade na célula do grid: a coluna é elástica (`minmax(160px, 1fr)`),
// então o corte por CSS varia com a largura da tela. O limite fixo mantém a linha
// previsível; o tooltip da célula continua com o nome completo.
export const UNIT_NAME_MAX_LENGTH = 16

// "Em análise" (UNDER_ANALYSIS) fica FORA do filtro de propósito: o estado
// existe na máquina de estados do PRD (Figura 02) e no enum do banco, mas
// nenhuma transição do backend o produz — não há ação, endpoint ou cron que
// execute `REVIEW → UNDER_ANALYSIS`. Oferecê-lo significava filtrar e sempre
// ver vazio. O `STATUS_CONFIG` mantém a entrada: se um dia o backend passar a
// produzi-lo, a linha renderiza o badge certo em vez do "Desconhecido".
export const STATUS_FILTER_OPTIONS = [
  { label: 'Aprovado', value: PostStationStatus.APPROVED },
  { label: 'Revisar', value: PostStationStatus.REVIEW },
  { label: 'Em validação', value: PostStationStatus.IN_EVALUATION },
  { label: 'Revertido', value: PostStationStatus.REVERTED }
]

// Título do estado de conformidade — fonte única para o tooltip do
// ComplianceIcon e para os aria-labels das células de domínio da tabela.
export const COMPLIANCE_STATE_TITLES: Record<ComplianceState, string> = {
  ok: 'Conforme',
  fail: 'Não conforme',
  pending: 'Não avaliado',
  not_required: 'Não exigido'
}

/* ------------------------------------------------------------------------- *
 * F4 — coluna e filtro "Reg. Plantão" (M5, balões ① e ⑤).
 * ------------------------------------------------------------------------- */

/**
 * Rótulos dos quatro estados. NÃO redeclarados aqui: o vocabulário do horário
 * efetivo tem UM dono (`EFFECTIVE_REGISTRATION_STATUS_LABELS`, F1), e uma
 * segunda cópia faria a mesma coluna dizer "Parcial" no grid e outra coisa no
 * Detalhes do Plantão.
 */
export const REGISTRATION_STATUS_LABELS = EFFECTIVE_REGISTRATION_STATUS_LABELS

/**
 * Quinto rótulo da coluna, e o único que não é um estado do registro: o domínio
 * ficou `PENDING` porque NÃO HAVIA política de presença vigente na data do
 * plantão (T7, balão ⑤). "Sem registro" seria mentira — ninguém avaliou.
 */
export const REGISTRATION_NOT_EVALUATED = REGISTRATION_NOT_EVALUATED_LABEL

/**
 * Cor de cada estado. `PENDING` é âmbar e não vermelho de propósito: há um
 * registro em auditoria a caminho, e pintá-lo de falha manda o gestor cobrar
 * algo que já está em curso.
 */
export const REGISTRATION_STATUS_COLORS: Record<
  EffectiveRegistrationStatus,
  PostStationStatusColor
> = {
  [EffectiveRegistrationStatus.OK]: 'green',
  [EffectiveRegistrationStatus.PARTIAL]: 'orange',
  [EffectiveRegistrationStatus.PENDING]: 'yellow',
  [EffectiveRegistrationStatus.NONE]: 'gray',
  [EffectiveRegistrationStatus.NOT_REQUIRED]: 'gray'
}

/** Opções do filtro do grid — multi-seleção; vazio = todos. */
export const REGISTRATION_STATUS_FILTER_OPTIONS = [
  { label: REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.OK], value: 'OK' },
  { label: REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.PARTIAL], value: 'PARTIAL' },
  { label: REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.PENDING], value: 'PENDING' },
  { label: REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NONE], value: 'NONE' },
  // Q1 — só depois do BE PR3 (o DTO do grid aceita o valor); antes disso o backend daria 400.
  {
    label: REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NOT_REQUIRED],
    value: 'NOT_REQUIRED'
  }
]

/**
 * Rótulo da coluna a partir do valor cru do backend. Valor desconhecido cai em
 * "Não avaliado" em vez de string vazia — uma célula em branco no grid lê como
 * bug, e um estado novo do backend é exatamente "ainda não sei classificar".
 */
export const registrationStatusLabelFor = (value?: string | null): string =>
  (value && REGISTRATION_STATUS_LABELS[value as EffectiveRegistrationStatus]) ||
  REGISTRATION_NOT_EVALUATED

/**
 * Estado do registro que a CÉLULA do grid deve pintar, ou `null` para cair no
 * comportamento de hoje (bolinha de conformidade com 3 estados).
 *
 * Devolve `null` em dois casos, e os dois significam a mesma coisa — "esta
 * versão do front não sabe classificar":
 *
 * 1. campo ausente — backend anterior à T9, ou a feature desligada.
 * 2. valor desconhecido — estado novo do backend que esta versão do front não
 *    sabe pintar; a bolinha de hoje é mais honesta que um ícone errado.
 *
 * F4-FX1 (revisto) — `registrationPolicyInEffect === false` NÃO cai mais aqui.
 * O backend continua avaliando e bloqueando esse plantão
 * (`blocking = policy?.postStationBlockOnRegistration ?? true`), e apagar a
 * pintura tirava a coluna do filtro que a trouxe: filtrar por "Sem registro"
 * devolvia linhas que não exibiam "Sem registro" (N6). A ausência de política
 * mudou de lugar: é RÓTULO (`registrationCellTitle`), não veredito.
 */
export const resolveRegistrationCellStatus = (
  row: Pick<PostStationRow, 'effectiveRegistrationStatus' | 'registrationPolicyInEffect'>
): RegistrationStatusValue | null => {
  const value = row.effectiveRegistrationStatus
  if (!value) return null

  return value in REGISTRATION_STATUS_LABELS ? (value as RegistrationStatusValue) : null
}

/**
 * Frase única do caso "não havia política de registro vigente na data". Vive
 * aqui para o grid, o card do Detalhes do Plantão e a sidebar dizerem
 * exatamente o mesmo — e nenhum deles afirmar efeito sobre a aprovação.
 */
export const REGISTRATION_NO_POLICY_PHRASE = 'sem política de registro vigente'

/**
 * Rótulo do estado de registro para leitores de tela e tooltip da célula.
 *
 * Sem política vigente o rótulo é "Não avaliado — sem política de registro
 * vigente" (balão ⑤ do M5): rotulagem, não veredito. A bolinha continua com a
 * cor que o backend decidiu, porque é ele quem decide.
 */
export const registrationCellTitle = (
  row: Pick<PostStationRow, 'effectiveRegistrationStatus' | 'registrationPolicyInEffect'>,
  fallbackState: ComplianceState
): string => {
  // Q1 — fora do time o registro não é exigido, com ou sem política vigente: "Não exigido"
  // vence "Não avaliado" (a ausência de política não muda nada para quem não é cobrado).
  if (
    row.effectiveRegistrationStatus === EffectiveRegistrationStatus.NOT_REQUIRED ||
    fallbackState === 'not_required'
  ) {
    return REGISTRATION_STATUS_LABELS[EffectiveRegistrationStatus.NOT_REQUIRED]
  }
  if (row.registrationPolicyInEffect === false) {
    return `${REGISTRATION_NOT_EVALUATED} — ${REGISTRATION_NO_POLICY_PHRASE}`
  }
  const status = resolveRegistrationCellStatus(row)
  return status ? REGISTRATION_STATUS_LABELS[status] : COMPLIANCE_STATE_TITLES[fallbackState]
}

/* ------------------------------------------------------------------------- *
 * F4 — gate configurável do pós-plantão (M5, balão ④) e congelamento (⑥/⑦).
 * ------------------------------------------------------------------------- */

/** Nome do toggle na aba "Política de registro de plantão" da escala. */
export const REGISTRATION_GATE_SETTING_NAME = 'Exigir Registro de Plantão completo'

export const REGISTRATION_INFORMATIVE_TITLE = 'Critério informativo'

export const REGISTRATION_INFORMATIVE_TEXT =
  `Nesta escala o Registro de Plantão não bloqueia a aprovação do pós-plantão ` +
  `(política: "${REGISTRATION_GATE_SETTING_NAME}" = Desligado).`

export const REGISTRATION_BLOCKED_TITLE = 'Aprovação bloqueada pela política'

/** Q1 (spec 2026-09-25 §2.2) — domínio de registro de plantão fora do time. */
export const REGISTRATION_NOT_REQUIRED_TITLE = 'Registro não exigido — médico fora do time'

/**
 * Texto do banner de bloqueio. A perna que falta é nomeada porque é a única
 * informação acionável: sem ela o gestor sabe que parou, não o que fazer.
 * Sem perna identificável (critério novo, backend antigo) o texto cai no
 * genérico em vez de mentir sobre qual perna falta.
 */
export const registrationBlockedText = (
  missingLegs: string[],
  policyInEffect: boolean = true
): string => {
  // Sem política vigente não há toggle a citar: atribuir o bloqueio a um ajuste
  // da escala seria a tela inventando a causa. O veredito (bloqueou) continua
  // sendo do backend — `blocking = policy?.postStationBlockOnRegistration ?? true`,
  // ou seja, a ausência de política bloqueia por padrão; só o "porquê" muda.
  const base = policyInEffect
    ? `"${REGISTRATION_GATE_SETTING_NAME}" está ligado nesta escala.`
    : `Este plantão está ${REGISTRATION_NO_POLICY_PHRASE} na data, e a ausência de política bloqueia por padrão.`
  if (missingLegs.length === 0) return `${base} O Registro de Plantão está incompleto.`
  if (missingLegs.length === 1) return `${base} Falta o ${missingLegs[0]}.`
  return `${base} Faltam o ${missingLegs.slice(0, -1).join(', o ')} e o ${missingLegs.at(-1)}.`
}

/**
 * F4-FX1 (revisto) — escala SEM política de registro vigente na data do
 * plantão. O texto é RÓTULO, não veredito.
 *
 * A F4-FX1 do backend removeu apenas o rebaixamento para `PENDING`; o
 * bloqueio continua (`blocking = policy?.postStationBlockOnRegistration ?? true`)
 * e `policyInEffect` é informativo. Afirmar aqui que "não bloqueia" fazia a
 * sidebar dizer o oposto do que o sistema fez. Por isso esta nota só aparece
 * quando o domínio NÃO reprovou — no caso reprovado quem fala é o banner.
 */
export const REGISTRATION_NOT_EVALUATED_TEXT =
  `Este plantão está ${REGISTRATION_NO_POLICY_PHRASE} na data, então o Registro de Plantão não ` +
  'recebeu um estado próprio. O efeito sobre a aprovação é sempre o que o pós-plantão indicar.'

/** Rótulo humano de cada perna nos critérios do domínio de registro. */
export const REGISTRATION_LEG_LABELS: Record<string, string> = {
  checkin: 'check-in',
  checkout: 'check-out'
}

/**
 * Q28 — o efeito é o MESMO para todo registro recebido depois do fechamento,
 * inclusive o aprovado: aprovar o pós-plantão congela o horário efetivo. Texto
 * único (e não uma coluna calculada) para nenhuma linha sugerir que "esta aqui
 * talvez conte".
 */
export const FROZEN_RECORD_EFFECT_TEXT = 'Recebido após fechamento — não altera o horário efetivo'

/** Complemento do texto da modal de reversão (M5-2): o que a reabertura faz. */
export const REVERT_REOPEN_EFFECT_TEXT =
  'A reabertura reverte a avaliação, recalcula o horário efetivo pelo ranking e devolve o ' +
  'plantão à fila para nova aprovação.'

export const DOMAIN_LABELS: Record<keyof PostStationCompliance, string> = {
  registration: 'Registro de plantão',
  qualification: 'Política Documental',
  repass: 'Qualificação para repasse',
  exception: 'Excepcionalidade',
  payment: 'Política de pagamento'
}

export const DOMAIN_CTA_LABELS: Record<keyof PostStationCompliance, string> = {
  registration: 'Ir para registro de plantão',
  qualification: 'Abrir política',
  repass: 'Abrir política',
  exception: 'Ver excepcionalidade',
  payment: 'Abrir módulo de origem'
}

// A UI usa as chaves históricas de PostStationCompliance; o path do drill-down usa
// as chaves kebab-case do contrato. O Record garante a cobertura dos dois lados.
export const DOMAIN_KEY_TO_API: Record<keyof PostStationCompliance, PostStationDomainKey> = {
  registration: 'registration',
  qualification: 'qualification-documental',
  repass: 'qualification-transfer',
  exception: 'exception',
  payment: 'payment'
}

// Não importar LabelBadgeColor do .vue: o build webpack (ts-loader) só vê o
// export default via shim — importar type nomeado quebra o build.
type ExceptionBadgeColor = 'green' | 'red' | 'blue' | 'gray' | 'orange'

export interface ExceptionBadgeConfig {
  label: string
  color: ExceptionBadgeColor
  icon: string
}

export const EXCEPTION_STATUS_BADGE: Record<string, ExceptionBadgeConfig> = {
  Aprovado: { label: 'Aprovado', color: 'green', icon: mdiCheckCircle },
  Recusado: { label: 'Reprovado', color: 'red', icon: mdiCloseCircle },
  Revertido: { label: 'Revertido', color: 'blue', icon: mdiArrowLeft },
  Cancelado: { label: 'Cancelado', color: 'gray', icon: mdiCloseCircle },
  'Aguardando aprovação': { label: 'Em andamento', color: 'orange', icon: mdiClockOutline },
  'Sem pedido': { label: 'Sem pedido', color: 'green', icon: mdiCheckCircle }
}

export const UNKNOWN_EXCEPTION_BADGE: ExceptionBadgeConfig = {
  label: 'Desconhecido',
  color: 'gray',
  icon: mdiHelpCircleOutline
}

export const exceptionBadgeFor = (actual: string): ExceptionBadgeConfig =>
  EXCEPTION_STATUS_BADGE[actual] ?? UNKNOWN_EXCEPTION_BADGE

/**
 * Texto da trava de edição (RN-07 / RF-12), aprovado no mockup da US-04.
 *
 * Compartilhado entre o banner do card de Pós-Plantão no detalhe do plantão
 * (`PostStationDetailsSection.vue`) e o banner do modal de edição
 * (`update.station.modal.vue`) — a mesma regra explicada com o mesmo texto nos
 * dois lugares onde o usuário a encontra.
 */
export const POST_STATION_LOCK_BANNER_TEXT =
  'Plantões aprovados para Pós-Plantão não podem ser editados. Para editá-lo, é necessário reverter a validação'
